import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icon';
import styles from './ManualFindBar.module.scss';

const MATCHES = 'manual-search';
const CURRENT = 'manual-search-current';

// Inline because the build's CSS parser rejects ::highlight(). Colours are
// $colorRed50 and $colorWhite.
const HIGHLIGHT_STYLES = `
    ::highlight(${MATCHES}) { background-color: rgba(140, 26, 17, 0.15); }
    ::highlight(${CURRENT}) { background-color: #8c1a11; color: #ffffff; }
`;

// Air between the sticky header and the match it scrolls to.
const SCROLL_GAP = 24;

// Lets a collapsed accordion finish opening before its match is scrolled to.
const REVEAL_DELAY_MS = 300;

const isSupported = () => typeof CSS !== 'undefined' && 'highlights' in CSS;

const findRanges = (root, query) => {
    const needle = query.toLowerCase();
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) =>
            node.parentElement.closest('nav, script, style, .sr-only')
                ? NodeFilter.FILTER_REJECT
                : NodeFilter.FILTER_ACCEPT,
    });
    const ranges = [];

    while (walker.nextNode()) {
        const node = walker.currentNode;
        const text = node.nodeValue.toLowerCase();
        let index = text.indexOf(needle);

        while (index !== -1) {
            const range = new Range();
            range.setStart(node, index);
            range.setEnd(node, index + needle.length);
            ranges.push(range);
            index = text.indexOf(needle, index + needle.length);
        }
    }

    return ranges;
};

const firstIndexAfter = (ranges, element) => {
    const index = ranges.findIndex(
        (range) =>
            element.contains(range.startContainer) ||
            element.compareDocumentPosition(range.startContainer) &
                Node.DOCUMENT_POSITION_FOLLOWING
    );

    return Math.max(index, 0);
};

// Returns whether any collapsed accordion had to be opened. Toggles are
// collected first since clicking one twice in a tick would close it again.
const reveal = (ranges) => {
    const toggles = new Set();

    ranges.forEach((range) => {
        for (
            let element = range.startContainer.parentElement;
            element;
            element = element.parentElement
        ) {
            const toggle =
                element.id &&
                document.querySelector(
                    `[aria-controls="${CSS.escape(
                        element.id
                    )}"][aria-expanded="false"]`
                );

            if (toggle) {
                toggles.add(toggle);
            }
        }
    });

    toggles.forEach((toggle) => toggle.click());

    return toggles.size > 0;
};

// The header is taller on small screens, so its height is read rather than
// assumed.
const scrollToRange = (range) => {
    const headerBottom =
        document.querySelector('header')?.getBoundingClientRect().bottom || 0;
    const top =
        range.getBoundingClientRect().top +
        window.scrollY -
        Math.max(headerBottom, 0) -
        SCROLL_GAP;
    window.scrollTo({ top });
};

const ManualFindBar = ({ query = '', containerRef = null, onClose }) => {
    const [ranges, setRanges] = useState([]);
    const [current, setCurrent] = useState(0);
    // Server and first client render stay empty, since only the browser has
    // CSS.highlights to decide with.
    const [isReady, setIsReady] = useState(false);
    const isOpening = useRef(false);

    useEffect(() => {
        setIsReady(isSupported());
    }, []);

    useEffect(() => {
        const root = containerRef?.current;

        if (!isReady || !root || !query) {
            return undefined;
        }

        const found = findRanges(root, query);
        const section = window.location.hash
            ? document.getElementById(
                  decodeURIComponent(window.location.hash.slice(1))
              )
            : null;

        isOpening.current = reveal(found);
        setRanges(found);
        setCurrent(section ? firstIndexAfter(found, section) : 0);
        CSS.highlights.set(MATCHES, new Highlight(...found));

        return () => {
            CSS.highlights.delete(MATCHES);
            CSS.highlights.delete(CURRENT);
        };
    }, [isReady, query, containerRef]);

    useEffect(() => {
        const range = ranges[current];

        if (!range) {
            return undefined;
        }

        CSS.highlights.set(CURRENT, new Highlight(range));

        const opened = reveal([range]) || isOpening.current;
        isOpening.current = false;

        const timer = setTimeout(
            () => scrollToRange(range),
            opened ? REVEAL_DELAY_MS : 0
        );

        return () => clearTimeout(timer);
    }, [ranges, current]);

    if (!isReady || !query) {
        return null;
    }

    const step = (direction) => {
        if (ranges.length) {
            setCurrent(
                (previous) =>
                    (previous + direction + ranges.length) % ranges.length
            );
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    };

    return (
        <>
            <div className={styles['ManualFindBar__Spacer']} />
            <div
                role="region"
                aria-label="Search matches on this page"
                className={styles['ManualFindBar']}
                onKeyDown={handleKeyDown}>
                <style>{HIGHLIGHT_STYLES}</style>
                <span className={styles['ManualFindBar__Query']}>
                    “{query}”
                </span>
                <span
                    className={styles['ManualFindBar__Count']}
                    role="status"
                    aria-live="polite">
                    {ranges.length
                        ? `${current + 1} / ${ranges.length}`
                        : 'No matches on this page'}
                </span>
                <button
                    type="button"
                    className={styles['ManualFindBar__Button']}
                    aria-label="Previous match"
                    disabled={ranges.length < 2}
                    onClick={() => step(-1)}>
                    <span className={styles['ManualFindBar__Previous']}>
                        <Icon
                            type="chevronThin"
                            modifiers={['ManualNavChevron']}
                        />
                    </span>
                </button>
                <button
                    type="button"
                    className={styles['ManualFindBar__Button']}
                    aria-label="Next match"
                    disabled={ranges.length < 2}
                    onClick={() => step(1)}>
                    <span className={styles['ManualFindBar__Next']}>
                        <Icon
                            type="chevronThin"
                            modifiers={['ManualNavChevron']}
                        />
                    </span>
                </button>
                <button
                    type="button"
                    className={styles['ManualFindBar__Button']}
                    aria-label="Clear search highlights"
                    onClick={onClose}>
                    <Icon type="crossSmall" />
                </button>
            </div>
        </>
    );
};

ManualFindBar.propTypes = {
    query: PropTypes.string,
    containerRef: PropTypes.shape({ current: PropTypes.any }),
    onClose: PropTypes.func.isRequired,
};

export default ManualFindBar;
